import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable, OnDestroy, inject } from '@angular/core';
import { FuseSidebarService } from '@fuse/components/sidebar/sidebar.service';
import { CustomHttpParamEncoder } from 'app/core/customHttpParamEncoder';
import { SidebarComponent } from 'app/layout/components/sidebar/sidebar.component';
import { PdfCreditTypeHelper } from 'app/shared/enum-helpers/pdf-credit-type-helper';
import { PdfDeliveryFormTypeHelper } from 'app/shared/enum-helpers/pdf-delivery-form-type-helper';
import { PdfFittingFormTypeHelper } from 'app/shared/enum-helpers/pdf-fitting-form-type-helper';
import { PdfInvoiceTypeHelper } from 'app/shared/enum-helpers/pdf-invoice-type-helper';
import { PdfProFormaTypeHelper } from 'app/shared/enum-helpers/pdf-pro-forma-type-helper';
import { PdfQuoteTypeHelper } from 'app/shared/enum-helpers/pdf-quote-type-helper';
import { PdfWorkFormTypeHelper } from 'app/shared/enum-helpers/pdf-work-form-type-helper';
import { Area } from 'app/shared/enums/area.enum';
import { NotificationAlertType } from 'app/shared/enums/notification-alert-type.enum';
import { NotificationStatus } from 'app/shared/enums/notification-status.enum';
import { PdfMergeType } from 'app/shared/enums/pdf-merge-type.enum';
import { NotificationFilter } from 'app/shared/prothesis-api-filters/notification-filter';
import { AddJournalNotificationViewModel } from 'app/shared/prothesis-api-view-models/add-journal-notification.view-model';
import { AddPdfMergeNotificationViewModel } from 'app/shared/prothesis-api-view-models/add-pdf-merge-notification.view-model';
import { AddSpreadsheetDashboardNotificationViewModel } from 'app/shared/prothesis-api-view-models/add-spreadsheet-dashboard-notification.view-model';
import { AddSpreadsheetExportNotificationViewModel } from 'app/shared/prothesis-api-view-models/add-spreadsheet-export-notification.view-model';
import { IndexNotificationViewModel } from 'app/shared/prothesis-api-view-models/index-notification.view-model';
import { ReadDocoonInboundFlowNotificationViewModel } from 'app/shared/prothesis-api-view-models/read-docoon-inbound-flow-notification.view-model';
import { ReadExportAllNotificationViewModel } from 'app/shared/prothesis-api-view-models/read-export-all-notification.view-model';
import { ReadJournalNotificationViewModel } from 'app/shared/prothesis-api-view-models/read-journal-notification-view-model';
import { ReadNotificationViewModel } from 'app/shared/prothesis-api-view-models/read-notification.view-model';
import { ReadPdfMergeNotificationViewModel } from 'app/shared/prothesis-api-view-models/read-pdf-merge-notification.view-model';
import { ReadSpreadsheetDashboardNotificationViewModel } from 'app/shared/prothesis-api-view-models/read-spreadsheet-dashboard-notification.view-model';
import { ReadSpreadsheetExportNotificationViewModel } from 'app/shared/prothesis-api-view-models/read-spreadsheet-export-notification.view-model';
import { SearchNotificationViewModel } from 'app/shared/prothesis-api-view-models/search-notification.view-model';
import { ListViewModel } from 'app/shared/prothesis-base-api-view-models/list-view-model';
import { BehaviorSubject, Observable, Subject, Subscription, timer } from 'rxjs';
import { distinctUntilChanged, filter, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { AuthService } from '../auth.service';
import { ConfigurationService } from '../configuration.service';
import { CrudServiceBase } from '../crud-service-base';
import { NotifyService } from '../notify/notify.service';
import { LaboratoryHttpHeaderModel } from './laboratory-http-header-model';
import { LaboratoryHttpHeaderService } from './laboratory-http-header.service';
import { WorkingLaboratoryService } from './working-laboratory.service';

@Injectable({
  providedIn: 'root',
})
export class NotificationService
  extends CrudServiceBase<ReadNotificationViewModel, IndexNotificationViewModel, NotificationFilter>
  implements OnDestroy
{
  public onNotificationRefreshed$: Observable<ReadNotificationViewModel[]>;
  public onPopUpNotificationReceived$: Observable<ReadNotificationViewModel[]>;
  public pullingSubsriber$: Observable<number> = timer(2000, 2000);
  protected _configurationService: ConfigurationService;
  protected _workingLaboratoryService = inject(WorkingLaboratoryService);
  private _authService = inject(AuthService);
  private _fuseSidebarService = inject(FuseSidebarService);
  private _httpHeaderLoadedFlag = false;
  private _laboratoryHttpHeaderService = inject(LaboratoryHttpHeaderService);
  private _laboratorySuspendedFlag = false;
  private _notifyService = inject(NotifyService);
  private _onNotificationRefreshed$ = new BehaviorSubject<ReadNotificationViewModel[]>(null!);
  private _onPopUpNotificationReceived$ = new BehaviorSubject<ReadNotificationViewModel[]>(null!);
  private _unsubscribeAll: Subject<void>;
  private subscription: Subscription | null;

  public constructor() {
    const _configurationService = inject(ConfigurationService);
    const _httpClient = inject(HttpClient);

    super(_httpClient, () => '/Notification/', _configurationService.ProthesisApiAddress);
    this._configurationService = _configurationService;

    this._unsubscribeAll = new Subject();

    this._workingLaboratoryService.exploredUuid$
      .pipe(
        takeUntil(this._unsubscribeAll),
        distinctUntilChanged(),
        filter((laboratoryUuid) => laboratoryUuid !== null && laboratoryUuid !== undefined),
        switchMap(() => {
          this.stopPulling();
          this._httpHeaderLoadedFlag = false;

          return this._laboratoryHttpHeaderService.onHttpHeaderChanged.pipe(
            take(1),
            tap((httpHeader) => {
              this._httpHeaderLoadedFlag = true;
              this._laboratorySuspendedFlag = httpHeader?.suspendedFlag || false;
            }),
          );
        }),
      )
      .subscribe(() => {
        this.startPulling();
      });

    this._workingLaboratoryService.exploredUuid$
      .pipe(
        takeUntil(this._unsubscribeAll),
        distinctUntilChanged(),
        filter((laboratoryUuid) => !laboratoryUuid),
      )
      .subscribe(() => {
        this.stopPulling();
        this._httpHeaderLoadedFlag = false;
      });

    this._laboratoryHttpHeaderService.onHttpHeaderChanged
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((laboratoryHttpHeader: LaboratoryHttpHeaderModel) => {
        if (laboratoryHttpHeader) {
          const wasNotLoaded = !this._httpHeaderLoadedFlag;
          this._httpHeaderLoadedFlag = true;
          this._laboratorySuspendedFlag = laboratoryHttpHeader.suspendedFlag || false;

          if (wasNotLoaded && !this._laboratorySuspendedFlag) {
            if (this._authService.area === Area.Laboratory) {
              this.startPulling();
            }
          }
        }
      });

    this.onNotificationRefreshed$ = this._onNotificationRefreshed$.asObservable();
    this.onPopUpNotificationReceived$ = this._onPopUpNotificationReceived$.asObservable();
  }

  protected get _actionUrl(): string {
    return `${this._configurationService.ProthesisApiAddress}/Laboratory/${this._workingLaboratoryService.tenantUuid}/Notification/`;
  }

  public static activeNotificationFlag(readNotificationViewmodel: ReadNotificationViewModel): boolean {
    if (
      readNotificationViewmodel.discriminator === 'DocoonInboundFlow' &&
      !(readNotificationViewmodel as ReadDocoonInboundFlowNotificationViewModel).status
    ) {
      return true;
    } else if (
      readNotificationViewmodel.discriminator === 'ExportAll' &&
      !(readNotificationViewmodel as ReadExportAllNotificationViewModel).status
    ) {
      return true;
    } else if (
      readNotificationViewmodel.discriminator === 'Journal' &&
      !(readNotificationViewmodel as ReadJournalNotificationViewModel).status
    ) {
      return true;
    } else if (
      readNotificationViewmodel.discriminator === 'PdfMerge' &&
      !(readNotificationViewmodel as ReadPdfMergeNotificationViewModel).status
    ) {
      return true;
    } else if (
      readNotificationViewmodel.discriminator === 'SpreadsheetExport' &&
      !(readNotificationViewmodel as ReadSpreadsheetExportNotificationViewModel).status
    ) {
      return true;
    } else if (
      readNotificationViewmodel.discriminator === 'SpreadsheetDashboard' &&
      !(readNotificationViewmodel as ReadSpreadsheetDashboardNotificationViewModel).status
    ) {
      return true;
    } else {
      return false;
    }
  }

  public static buildPdfMergeNotifyMessage(addPdfMergeNotificationViewModel: AddPdfMergeNotificationViewModel): string | undefined {
    let notifyMessage: string | undefined = 'PDF multiple en cours de génération';
    const counter = addPdfMergeNotificationViewModel.uuids.length;
    const pdfDocumentType = addPdfMergeNotificationViewModel.pdfDocumentType;

    if (addPdfMergeNotificationViewModel.pdfMergeType === PdfMergeType.deliveryForm) {
      notifyMessage = PdfDeliveryFormTypeHelper.buildPdfMergeNotifyMessage(counter, pdfDocumentType);
    } else if (addPdfMergeNotificationViewModel.pdfMergeType === PdfMergeType.credit) {
      notifyMessage = PdfCreditTypeHelper.buildPdfMergeNotifyMessage(counter, pdfDocumentType);
    } else if (addPdfMergeNotificationViewModel.pdfMergeType === PdfMergeType.fittingForm) {
      notifyMessage = PdfFittingFormTypeHelper.buildPdfMergeNotifyMessage(counter, pdfDocumentType);
    } else if (addPdfMergeNotificationViewModel.pdfMergeType === PdfMergeType.invoice) {
      notifyMessage = PdfInvoiceTypeHelper.buildPdfMergeNotifyMessage(counter, pdfDocumentType);
    } else if (addPdfMergeNotificationViewModel.pdfMergeType === PdfMergeType.proForma) {
      notifyMessage = PdfProFormaTypeHelper.buildPdfMergeNotifyMessage(counter, pdfDocumentType);
    } else if (addPdfMergeNotificationViewModel.pdfMergeType === PdfMergeType.quote) {
      notifyMessage = PdfQuoteTypeHelper.buildPdfMergeNotifyMessage(counter, pdfDocumentType);
    } else if (addPdfMergeNotificationViewModel.pdfMergeType === PdfMergeType.workForm) {
      notifyMessage = PdfWorkFormTypeHelper.buildPdfMergeNotifyMessage(counter, pdfDocumentType);
    }

    return notifyMessage;
  }

  public static finishedNotificationFlag(readNotificationViewmodel: ReadNotificationViewModel): boolean {
    if (
      readNotificationViewmodel.discriminator === 'DocoonInboundFlow' &&
      ((readNotificationViewmodel as ReadDocoonInboundFlowNotificationViewModel).status === NotificationStatus.Succeed ||
        (readNotificationViewmodel as ReadDocoonInboundFlowNotificationViewModel).status === NotificationStatus.Failed)
    ) {
      return true;
    } else if (
      readNotificationViewmodel.discriminator === 'ExportAll' &&
      ((readNotificationViewmodel as ReadExportAllNotificationViewModel).status === NotificationStatus.Succeed ||
        (readNotificationViewmodel as ReadExportAllNotificationViewModel).status === NotificationStatus.Failed)
    ) {
      return true;
    } else if (
      readNotificationViewmodel.discriminator === 'Journal' &&
      ((readNotificationViewmodel as ReadJournalNotificationViewModel).status === NotificationStatus.Succeed ||
        (readNotificationViewmodel as ReadJournalNotificationViewModel).status === NotificationStatus.Failed)
    ) {
      return true;
    } else if (
      readNotificationViewmodel.discriminator === 'PdfMerge' &&
      ((readNotificationViewmodel as ReadPdfMergeNotificationViewModel).status === NotificationStatus.Succeed ||
        (readNotificationViewmodel as ReadPdfMergeNotificationViewModel).status === NotificationStatus.Failed)
    ) {
      return true;
    } else if (
      readNotificationViewmodel.discriminator === 'SpreadsheetExport' &&
      ((readNotificationViewmodel as ReadSpreadsheetExportNotificationViewModel).status === NotificationStatus.Succeed ||
        (readNotificationViewmodel as ReadSpreadsheetExportNotificationViewModel).status === NotificationStatus.Failed)
    ) {
      return true;
    } else if (
      readNotificationViewmodel.discriminator === 'SpreadsheetDashboard' &&
      ((readNotificationViewmodel as ReadSpreadsheetDashboardNotificationViewModel).status === NotificationStatus.Succeed ||
        (readNotificationViewmodel as ReadSpreadsheetDashboardNotificationViewModel).status === NotificationStatus.Failed)
    ) {
      return true;
    } else {
      return false;
    }
  }

  public static unreadNotificationFlag(readNotificationViewmodel: ReadNotificationViewModel): boolean {
    if (!readNotificationViewmodel.readDate || readNotificationViewmodel.readDate < readNotificationViewmodel.notificationDate) {
      return true;
    } else {
      return false;
    }
  }

  public delete(uuid: string | null | undefined): Observable<void> {
    // Suppress the display of messages by the ApiInterceptor, as error handling will be managed here.
    this._notifyService.flagUp();

    return new Observable((observer) => {
      this._delete(uuid).subscribe(
        () => {
          this._lookForNotificiation();
          observer.next();
          observer.complete();
        },
        () => {
          observer.next();
          observer.complete();
        },
      );
    });
  }

  public deleteCollection(notificationUuids: string[] | null | undefined): Observable<void> {
    // Suppress the display of messages by the ApiInterceptor, as error handling will be managed here.
    this._notifyService.flagUp();

    return new Observable((observer) => {
      this._deleteCollection(notificationUuids).subscribe(
        () => {
          this._lookForNotificiation();
          observer.next();
          observer.complete();
        },
        () => {
          observer.next();
          observer.complete();
        },
      );
    });
  }

  public generateDocoonInboundFlow(): Observable<boolean> {
    return new Observable((observer) => {
      this._httpClient.post<string>(`${this._actionUrl}GenerateDocoonInboundFlow`, '').subscribe(
        () => {
          // The pending notification is a popup: the next poll opens the sidebar on it.
          this.startPulling();
          observer.next(true);
          observer.complete();
        },
        () => {
          observer.next(false);
          observer.complete();
        },
      );
    });
  }

  public generateExportAll(): Observable<boolean> {
    return new Observable((observer) => {
      this._httpClient.post<string>(`${this._actionUrl}GenerateExportAll`, '').subscribe(
        () => {
          this.startPulling();
          observer.next(true);
          observer.complete();
        },
        () => {
          observer.next(false);
          observer.complete();
        },
      );
    });
  }

  public generateJournal(addJournalNotificationViewModel: AddJournalNotificationViewModel): Observable<boolean> {
    return new Observable((observer) => {
      this._httpClient.post<string>(`${this._actionUrl}GenerateJournal`, addJournalNotificationViewModel).subscribe(
        () => {
          this.startPulling();
          observer.next(true);
          observer.complete();
        },
        () => {
          observer.next(false);
          observer.complete();
        },
      );
    });
  }

  public generatePdfMerge(addPdfMergeNotificationViewModel: AddPdfMergeNotificationViewModel): Observable<boolean> {
    return new Observable((observer) => {
      this._httpClient
        .post<AddPdfMergeNotificationViewModel>(`${this._actionUrl}GeneratePdfMerge`, addPdfMergeNotificationViewModel)
        .subscribe(
          () => {
            const notifyMessage = NotificationService.buildPdfMergeNotifyMessage(addPdfMergeNotificationViewModel);
            this._notifyService.doNotDismissOnNextApiCall();
            this._notifyService.displayInformationMessagesNow(notifyMessage);
            this.startPulling();
            observer.next(true);
            observer.complete();
          },
          () => {
            observer.next(false);
            observer.complete();
          },
        );
    });
  }

  public generateSpreadsheetDashboard(
    addSpreadsheetDashboardNotificationViewModel: AddSpreadsheetDashboardNotificationViewModel,
  ): Observable<boolean> {
    return new Observable((observer) => {
      this._httpClient
        .post<AddSpreadsheetDashboardNotificationViewModel>(
          `${this._actionUrl}GenerateSpreadsheetDashboard`,
          addSpreadsheetDashboardNotificationViewModel,
        )
        .subscribe(
          () => {
            this.startPulling();
            observer.next(true);
            observer.complete();
          },
          () => {
            observer.next(false);
            observer.complete();
          },
        );
    });
  }

  public generateSpreadsheetExport(
    addSpreadsheetExportNotificationViewModel: AddSpreadsheetExportNotificationViewModel,
  ): Observable<boolean> {
    return new Observable((observer) => {
      this._httpClient
        .post<AddSpreadsheetExportNotificationViewModel>(
          `${this._actionUrl}GenerateSpreadsheetExport`,
          addSpreadsheetExportNotificationViewModel,
        )
        .subscribe(
          () => {
            this.startPulling();
            observer.next(true);
            observer.complete();
          },
          () => {
            observer.next(false);
            observer.complete();
          },
        );
    });
  }

  public getExportAll(uuid: string | null | undefined): Observable<HttpResponse<Blob>> {
    return this._httpClient.get(`${this._actionUrl}${uuid}/GetExportAll`, { observe: 'response', responseType: 'blob' });
  }

  public getJournalFec(uuid: string | null | undefined): Observable<HttpResponse<Blob>> {
    return this._httpClient.get(`${this._actionUrl}${uuid}/GetJournalFec`, { observe: 'response', responseType: 'blob' });
  }

  public getJournalPdf(uuid: string | null | undefined): Observable<HttpResponse<Blob>> {
    return this._httpClient.get(`${this._actionUrl}${uuid}/GetJournalPdf`, { observe: 'response', responseType: 'blob' });
  }

  public getJournalSpreadsheet(uuid: string | null | undefined): Observable<HttpResponse<Blob>> {
    return this._httpClient.get(`${this._actionUrl}${uuid}/GetJournalSpreadsheet`, { observe: 'response', responseType: 'blob' });
  }

  public getJournalZip(uuid: string): Observable<HttpResponse<Blob>> {
    return this._httpClient.get(`${this._actionUrl}${uuid}/GetJournalZip`, { observe: 'response', responseType: 'blob' });
  }

  public getMergedPdf(uuid: string | null | undefined): Observable<HttpResponse<Blob>> {
    return this._httpClient.get(`${this._actionUrl}${uuid}/GetMergedPdf`, { observe: 'response', responseType: 'blob' });
  }

  public getMergedZip(uuid: string | null | undefined): Observable<HttpResponse<Blob>> {
    return this._httpClient.get(`${this._actionUrl}${uuid}/GetMergedZip`, { observe: 'response', responseType: 'blob' });
  }

  public getSpreadsheetDashboard(uuid: string | null | undefined): Observable<HttpResponse<Blob>> {
    return this._httpClient.get(`${this._actionUrl}${uuid}/GetSpreadsheetDashboard`, { observe: 'response', responseType: 'blob' });
  }

  public getSpreadsheetExport(uuid: string | null | undefined): Observable<HttpResponse<Blob>> {
    return this._httpClient.get(`${this._actionUrl}${uuid}/GetSpreadsheetExport`, { observe: 'response', responseType: 'blob' });
  }

  public initializeSearch(
    filter: NotificationFilter,
    sort: string | null,
    page: number,
    pageSize: number,
  ): Observable<SearchNotificationViewModel> {
    if (!filter) {
      filter = new NotificationFilter();
    }

    const params = new HttpParams({ encoder: new CustomHttpParamEncoder() })
      .set('filter', JSON.stringify(filter))
      .set('page', page.toString())
      .set('pageSize', pageSize.toString())
      .set('sort', sort!);

    return this._httpClient.get<SearchNotificationViewModel>(`${this._actionUrl}InitializeSearch`, { params: params });
  }

  public markAsReadCollection(readNotificationViewModels: ReadNotificationViewModel[]): Observable<boolean> {
    // Suppress the display of messages by the ApiInterceptor, as error handling will be managed here.
    this._notifyService.flagUp();

    const readViewModels = new ListViewModel<ReadNotificationViewModel>();
    readViewModels.values = readNotificationViewModels;

    return new Observable<boolean>((observer) => {
      this._httpClient
        .post<ListViewModel<ReadNotificationViewModel>>(`${this._actionUrl}MarkAsReadCollection`, JSON.stringify(readViewModels))
        .subscribe(
          () => {
            this._lookForNotificiation();
            observer.next(true);
            observer.complete();
          },
          () => {
            observer.next(false);
            observer.complete();
          },
        );
    });
  }

  public ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  public startPulling(): void {
    if (this.subscription) {
      return;
    }

    this._lookForNotificiation();
    if (this._workingLaboratoryService.tenantUuid) {
      this.subscription = this.pullingSubsriber$.subscribe(() => {
        this._lookForNotificiation();
      });
    }
  }

  public stopPulling(): void {
    this.subscription?.unsubscribe();
    this.subscription = null;
  }

  private _lookForNotificiation(): void {
    if (!this._httpHeaderLoadedFlag || !this._workingLaboratoryService.tenantUuid) {
      return;
    }
    if (this._laboratorySuspendedFlag) {
      this.stopPulling();
      return;
    }
    this._notifyService.doNotDismissOnNextApiCall();
    const filter = new NotificationFilter();

    this.initializeSearch(filter, null, 0, 100).subscribe(
      (result) => {
        if (result.resultList?.values) {
          this._onNotificationRefreshed$.next(result.resultList.values);
        }

        if (result.resultList?.values?.length > 0) {
          // Stop pulling if there are no more active notifications
          const activeNotifications = result.resultList.values.filter((x) => NotificationService.activeNotificationFlag(x));
          if (activeNotifications?.length === 0) {
            this.stopPulling();
          }

          // Open the sidebar if there is at least one unread popup notification.
          const unreadPopupNotifications = result.resultList.values.filter(
            (x) => NotificationService.unreadNotificationFlag(x) && x.alertType === NotificationAlertType.Popup,
          );

          if (unreadPopupNotifications.length > 0) {
            this._fuseSidebarService.getSidebar(SidebarComponent.key)?.toggleOpenOnly();
            this._onPopUpNotificationReceived$.next(unreadPopupNotifications);
          }
        } else {
          this.stopPulling();
        }
      },
      (error) => {
        console.error(error);
        this.stopPulling();
      },
    );
  }
}
